import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import TopHeadding from "../../components/layout/TopHeadding";
import plus_icon from "../../assets/images/plus_icon.svg";
import export_icon from "../../assets/images/export_icon.svg";
import FilterTopBar from "../../components/navigation/FilterTopBar";

import ClientsList from "../../components/tables/ClientsListTable";

import { useAppDispatch } from "../../hooks/useAppDispatch";
import { useAppSelector } from "../../hooks/useAppSelector";

import {
  fetchAllClients,
  exportClientsCSV
} from "../../store/slices/userSlice";

const Client = () => {

  const PAGE_KEY = "mainClientListingPage";

  const { user, role, exportCSVLoading } = useAppSelector((s) => s.user);

  const listData = useAppSelector((s) => s.user.listDataByKey[PAGE_KEY] || []);
  const loading = useAppSelector((s) => s.user.listLoadingByKey[PAGE_KEY] || false);
  const totalRecords = useAppSelector((s) => s.user.totalByKey[PAGE_KEY] || 0);

  const dispatch = useAppDispatch();

  const [search, setSearch] = useState("");

  const navigate = useNavigate();
  const location = useLocation();

  const [pageIndex, setPageIndex] = useState(1);
  const pageSize = 10;

  // Filter states
  const [filters, setFilters] = useState({
    startDate: null,
    endDate: null,
    payRange: [0, 1000000],
    status: true
  });

  const setFiltersStable = useCallback((newFilters) => {
    setFilters((prev) => {
      if (
        prev.startDate === newFilters.startDate &&
        prev.endDate === newFilters.endDate &&
        prev.payRange[0] === newFilters.payRange[0] &&
        prev.payRange[1] === newFilters.payRange[1] &&
        prev.status === newFilters.status
      ) {
        return prev;
      }
      return newFilters;
    });
  }, []);

  const memoizedFilters = useMemo(() => filters, [filters.startDate, filters.endDate, filters.payRange[0], filters.payRange[1], filters.status]);

  const buildCompanyParams = useCallback((extra = {}) => {
    const params = {
      startDate: filters.startDate ?? null,
      endDate: filters.endDate ?? null,
      status: filters.status,
      minPayAvg: filters.payRange[0],
      maxPayAvg: filters.payRange[1],
      ...extra,
    };

    if (search) {
      params.search = search;
    }

    return params;
  }, [filters, search]);

  useEffect(() => {
    if (!user || !role) return;

    const params = {
      startDate: memoizedFilters.startDate ?? null,
      endDate: memoizedFilters.endDate ?? null,
      status: memoizedFilters.status,
      minPayAvg: memoizedFilters.payRange[0],
      maxPayAvg: memoizedFilters.payRange[1],
      pageIndex: pageIndex - 1,
      pageSize,
    };

    if (search) {
      params.search = search;
    }

    dispatch(fetchAllClients({ params, key: PAGE_KEY }));

  }, [user, role, search, dispatch, pageIndex, memoizedFilters]);

  useEffect(() => {
    setPageIndex(1);
  }, [search, memoizedFilters]);

  useEffect(() => {
    const maxPage = Math.ceil(totalRecords / pageSize) || 1;
    if (pageIndex > maxPage) {
      setPageIndex(maxPage);
    }
  }, [totalRecords, pageIndex, pageSize]);

  return (
    <div className="Clints">
      <TopHeadding
        title={`Client List`}
        spanText={totalRecords ? `(${totalRecords})` : ""}
        subtitle=""
        showDatepicker={false}
        buttons={[
          {
            text: exportCSVLoading ? "Exporting" : "Export",
            className: "btn_secondary",
            icon: exportCSVLoading ? null : export_icon,
            disabled: exportCSVLoading,
            onClick: () => {
              if (role === "SuperAdmin" && !exportCSVLoading) {
                const params = buildCompanyParams();
                dispatch(exportClientsCSV(params));
              }
            },
          },
          {
            text:
              role === "SuperAdmin"
                ? "Add Client"
                : role === "Client"
                  ? "Add Company"
                  : "Add Employee",
            className: "btn_primary",
            icon: plus_icon,
            onClick: () => navigate('/addnewclient', { state: { from: location.pathname } }),
          },
        ]}
      />

      <div className="clint_table mt_30">
        <div className="container-fluid">
          <div className="row">
            <div className="col-md-12">
              <div className="white_box table_box flex-fill py_20 px_20">

                {/* 🔹 SEARCH + FILTER ROW */}
                <FilterTopBar
                  search={search}
                  onSearchChange={setSearch}
                  placeholder="Search Clients"
                  showSearch={true}
                  showFilter={true}
                  showExport={false}
                  onFilterChange={setFiltersStable}
                  filterClassName="ms-auto"
                />

                {/* <hr className="my-3" /> */}

                {/* 🔹 TABLE */}
                <ClientsList
                  className="custom_table_list"
                  data={listData || []}
                  loading={loading}
                  currentPage={pageIndex}
                  pageSize={pageSize}
                  totalRecords={totalRecords}
                  onPageChange={(pageNumber) => setPageIndex(pageNumber)}
                  pageKey={PAGE_KEY}
                />

              </div>
            </div>
          </div>
        </div>
      </div>

    </div>
  );
};

export default Client;
